import { endOfDay, parse, startOfDay } from 'date-fns';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

import {
  BULK_SEND_BE_STATUS,
  BULK_SEND_DATE_VALUE_FORMAT,
  BULK_SEND_TASK_TYPE,
} from '../../../constants/bulkSendManager';
import { IBulkSendFilters, TBulkSendStatus } from '../../../types/bulkSendManager';
import { TUserTaskStatus } from '../../../types/userTask';

export interface IBulkSendWhereClause {
  field: string;
  op: firebase.firestore.WhereFilterOp;
  value: unknown;
}

export interface IBulkSendQueryScope {
  userId: string;
  orgId: string;
}

export const toBeStatuses = (statuses: TBulkSendStatus[]): TUserTaskStatus[] =>
  statuses.reduce<TUserTaskStatus[]>((acc, status) => {
    const beStatus = BULK_SEND_BE_STATUS[status];

    return acc.includes(beStatus) ? acc : [...acc, beStatus];
  }, []);

const toDayBoundary = (
  value: string | null | undefined,
  boundary: (date: Date) => Date,
): firebase.firestore.Timestamp | null =>
  value ? firebase.firestore.Timestamp.fromDate(boundary(parse(value, BULK_SEND_DATE_VALUE_FORMAT, new Date()))) : null;

export const buildBulkSendsScopeClauses = (scope: IBulkSendQueryScope): IBulkSendWhereClause[] => [
  { field: 'user_id', op: '==', value: scope.userId },
  { field: 'org_id', op: '==', value: scope.orgId },
  { field: 'type', op: '==', value: BULK_SEND_TASK_TYPE },
];

export const buildBulkSendsWhereClauses = (
  scope: IBulkSendQueryScope,
  filters: IBulkSendFilters,
): IBulkSendWhereClause[] => {
  const clauses = buildBulkSendsScopeClauses(scope);

  const beStatuses = toBeStatuses(filters.statuses);

  if (beStatuses.length > 0) {
    clauses.push({ field: 'status', op: 'in', value: beStatuses });
  }

  const from = toDayBoundary(filters.dateRange?.from, startOfDay);
  const to = toDayBoundary(filters.dateRange?.to, endOfDay);

  if (from) {
    clauses.push({ field: 'created_at', op: '>=', value: from });
  }

  if (to) {
    clauses.push({ field: 'created_at', op: '<=', value: to });
  }

  return clauses;
};
